import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Location } from '@/lib/types';

import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import type { LayerVisibility } from '@/components/map-creator/canvas/types';
import { useMapAssets } from '@/components/map-creator/hooks/use-map-assets';
import { useMapLabels } from '@/components/map-creator/hooks/use-map-labels';
import { useMapPaths } from '@/components/map-creator/hooks/use-map-paths';
import { useMapWorld } from '@/components/map-creator/hooks/use-map-world';
import { MapCanvas, type DrawingTool, type LandTool, type MapCanvasHandle, type MapSection, type SymbolMode } from '@/components/map-creator/map-canvas';
import { ToolbarPanel, ToolbarSidebar } from '@/components/map-creator/toolbar';
import { WorldEntityPicker } from '@/components/map-creator/toolbar/WorldEntityPicker';
import { PageHeader } from '@/components/page-header';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useEntity } from '@/hooks/use-entity';
import { useMapHistory } from '@/hooks/use-map-history';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/locations/map/$id')({
    component: MapCreatorPage,
});

interface PlacedSymbol {
    uid: string;
    id: string;
    x: number;
    y: number;
    size: number;
    rotation?: number;
}

function capitalizeFirstLetter(string: string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function MapCreatorContent({ locationId }: { locationId: string }) {
    const { t } = useTranslation();
    const { backend, user } = useFirebase();
    const { novel } = useNovel();
    const novelId = novel?.id;

    const [viewMode, setViewMode] = useState<'settings' | 'canvas'>('settings');
    const [mapType, setMapType] = useState<string | undefined>(undefined);
    const [mapStyle, setMapStyle] = useState<string>('fantasy');
    const [activeSection, setActiveSection] = useState<MapSection>('land');
    const [layerVisibility, setLayerVisibility] = useState<LayerVisibility>({
        symbols: true,
        rivers: true,
        paths: true,
        pinpoints: false,
        areas: false,
        labels: true,
        grid: false,
        gridType: 'square',
        gridSize: 50,
    });
    const [activeLandTool, setActiveLandTool] = useState<LandTool>('raise');
    const [paintOpacity, setPaintOpacity] = useState(1);
    const [isSaving, setIsSaving] = useState(false);

    // Brush States
    const [brush, setBrush] = useState('land');
    const [brushSize, setBrushSize] = useState(5);
    const [roughness, setRoughness] = useState(3);
    const [tool, setTool] = useState<DrawingTool>('pencil');
    const [activeSymbol, setActiveSymbol] = useState<string>('oak_1');
    const [activeSymbols, setActiveSymbols] = useState<string[]>(['oak_1']);
    const [activeSymbolCategory, setActiveSymbolCategory] = useState<string>('trees');
    const [symbolMode, setSymbolMode] = useState<SymbolMode>('single');
    const [stampDensity, setStampDensity] = useState(0.3);
    const [symbolSize, setSymbolSize] = useState(20);

    // Placed Symbols State
    const [placedSymbols, setPlacedSymbols] = useState<PlacedSymbol[]>([]);

    const { data: location, isLoading: isDocLoading } = useEntity<Location>('locations', locationId);
    const [isCheckingForMap, setIsCheckingForMap] = useState(true);

    // World entity state and logic
    const world = useMapWorld(location?.mapEntityPoints ?? undefined, location?.mapEntityAreas ?? undefined);
    // Path state and logic
    const paths = useMapPaths(location?.mapEntityPaths ?? undefined);
    // Label state and logic
    const labels = useMapLabels(location?.mapLabels ?? undefined);

    const {
        landRef, colorRef, symbolsRef, assemblerRef, patternsRef, landMaskRef, stampMaskRef,
        customMapImageRef,
        isLoading: isAssetsLoading, lastUpdated, processedSheets
    } = useMapAssets(location);

    const [historyTick, setHistoryTick] = useState(0);

    const getSnapshot = useCallback(() => {
        const lCtx = landRef.current?.getContext('2d');
        const cCtx = colorRef.current?.getContext('2d');
        return {
            placedSymbols: [...placedSymbols],
            mapEntityPoints: [...world.mapEntityPoints],
            mapEntityAreas: [...world.mapEntityAreas],
            mapEntityPaths: [...paths.mapEntityPaths],
            mapLabels: [...labels.mapLabels],
            landImageData: lCtx && landRef.current ? lCtx.getImageData(0, 0, landRef.current.width, landRef.current.height) : null,
            colorImageData: cCtx && colorRef.current ? cCtx.getImageData(0, 0, colorRef.current.width, colorRef.current.height) : null,
            landMaskData: landMaskRef.current ? new Uint8Array(landMaskRef.current) : null,
        };
    }, [placedSymbols, world.mapEntityPoints, world.mapEntityAreas, paths.mapEntityPaths, labels.mapLabels, landRef, colorRef, landMaskRef]);

    const restoreSnapshot = useCallback((state: any) => {
        setPlacedSymbols(state.placedSymbols);
        world.setMapEntityPoints(state.mapEntityPoints);
        world.setMapEntityAreas(state.mapEntityAreas);
        paths.setMapEntityPaths(state.mapEntityPaths);
        labels.setMapLabels(state.mapLabels);

        if (state.landImageData && landRef.current) {
            landRef.current.getContext('2d')?.putImageData(state.landImageData, 0, 0);
        }
        if (state.colorImageData && colorRef.current) {
            colorRef.current.getContext('2d')?.putImageData(state.colorImageData, 0, 0);
        }
        if (state.landMaskData && landMaskRef.current) {
            landMaskRef.current.set(state.landMaskData);
        }
        setHistoryTick(t => t + 1);
    }, [world, paths, labels, landRef, colorRef, landMaskRef]);

    const { commitHistory, undo, redo, canUndo, canRedo, clearHistory } = useMapHistory(getSnapshot, restoreSnapshot, 5);
    const canvasRef = useRef<MapCanvasHandle>(null);
    const { toast } = useToast();

    // Sync placedSymbols from document
    useEffect(() => {
        if (location && location.placedSymbols) {
            setPlacedSymbols(location.placedSymbols);
        } else {
            setPlacedSymbols([]);
        }
    }, [location?.placedSymbols]);

    // Sync world entity objects from document

    // Force default section to 'symbols' for custom uploaded maps, disabling 'land'
    useEffect(() => {
        if (location?.originalMapUrl && activeSection === 'land') {
            setActiveSection('symbols');
        }
    }, [location?.originalMapUrl, activeSection]);

    // TODO: erase tomorrow
    // Legacy migration cleanup
    useEffect(() => {
        if (isDocLoading || !location || !novel.id) return;

        const hasLegacyMap = location.mapLandUrl && !location.placedSymbols;
        if (hasLegacyMap) {
            const ownerId = novel.ownerId || user?.uid;
            if (ownerId && novel.id) {
                backend.entities.updateEntity(ownerId, novel.id, 'locations', locationId, {
                    mapLandUrl: null,
                    mapLandStoragePath: null,
                    mapColorUrl: null,
                    mapColorStoragePath: null,
                    mapSymbolsUrl: null,
                    mapSymbolsStoragePath: null,
                    mapImageUrl: null,
                    mapImageStoragePath: null,
                    placedSymbols: null
                }).catch(err => {
                    console.error("Failed to delete legacy map data:", err);
                });
            }
        }
    }, [location, isDocLoading, novel, user?.uid, backend, locationId]);

    useEffect(() => {
        if (isDocLoading) return;

        if (location?.mapData || location?.mapImageUrl || location?.mapLandUrl) {
            setViewMode('canvas');
        }
        setIsCheckingForMap(false);
    }, [location, isDocLoading]);

    // Clear history on initial asset load
    useEffect(() => {
        if (!isAssetsLoading) {
            clearHistory();
        }
    }, [isAssetsLoading, clearHistory]);

    // Keyboard shortcuts for Undo/Redo
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if in input field
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    if (canRedo) redo();
                } else {
                    if (canUndo) undo();
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
                e.preventDefault();
                if (canRedo) redo();
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                let deleted = false;
                if (activeSection === 'paths' && paths.selectedPathUid && !paths.isDrawingPath) {
                    e.preventDefault();
                    paths.deletePath(paths.selectedPathUid);
                    deleted = true;
                } else if (activeSection === 'world' && world.selectedWorldUid && !world.isDrawingArea && !world.isPlacingPoint) {
                    e.preventDefault();
                    world.deletePoint(world.selectedWorldUid);
                    world.deleteArea(world.selectedWorldUid);
                    deleted = true;
                } else if (activeSection === 'labels' && labels.selectedLabelUid) {
                    e.preventDefault();
                    labels.deleteLabel(labels.selectedLabelUid);
                    deleted = true;
                }
                if (deleted) commitHistory();
            } else if (e.key === 'Escape') {
                if (activeSection === 'paths' && paths.selectedPathUid && !paths.isDrawingPath) {
                    paths.setSelectedPathUid(null);
                } else if (activeSection === 'world' && world.selectedWorldUid && !world.isDrawingArea && !world.isPlacingPoint) {
                    world.setSelectedWorldUid(null);
                } else if (activeSection === 'labels' && labels.selectedLabelUid) {
                    labels.setSelectedLabelUid(null);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canUndo, canRedo, undo, redo, activeSection, paths, world, labels, commitHistory]);

    const handleSaveGrid = async () => {
        if (!canvasRef.current || !backend || !user || !novelId || !novel.id) return;

        setIsSaving(true);
        try {
            const { land, color, symbols } = await canvasRef.current.getBuffers();
            const composite = await canvasRef.current.getComposite();

            if (!land || !color || !symbols || !composite) throw new Error("Could not capture map layers.");

            if (!novel.id) {
                throw new Error("Save error: novel.id is missing.");
            }
            if (!locationId) {
                throw new Error("Save error: locationId is missing.");
            }
            const ownerId = novel.ownerId || user.uid;
            if (!ownerId) {
                throw new Error("Save error: ownerId is missing.");
            }

            const subfolder = `locations/${locationId}/map`;
            const isCustomUpload = !!location?.originalMapUrl;

            // Capture old paths for storage cleanup
            const oldLandPath = location?.mapLandStoragePath;
            const oldColorPath = location?.mapColorStoragePath;
            const oldSymbolsPath = location?.mapSymbolsStoragePath;
            const oldCompositePath = location?.mapImageStoragePath;
            const oldOriginalPath = location?.originalMapStoragePath;

            let landUrl = null;
            let landPath = null;
            let symbolsUrl = null;
            let symbolsPath = null;

            if (!isCustomUpload) {
                const { url: lUrl, path: lPath } = await backend.assets.uploadImage(land, novel.id, subfolder);
                const { url: sUrl, path: sPath } = await backend.assets.uploadImage(symbols, novel.id, subfolder);
                landUrl = lUrl;
                landPath = lPath;
                symbolsUrl = sUrl;
                symbolsPath = sPath;
            }

            const { url: colorUrl, path: colorPath } = await backend.assets.uploadImage(color, novel.id, subfolder);
            const { url: compositeUrl, path: compositePath } = await backend.assets.uploadImage(composite, novel.id, subfolder);

            // Uploaded-map coordinates use the image's natural pixel space; procedural maps use 1024x1024.
            const customMapImage = customMapImageRef.current;
            const savedMapWidth = isCustomUpload && customMapImage ? customMapImage.naturalWidth : 1024;
            const savedMapHeight = isCustomUpload && customMapImage ? customMapImage.naturalHeight : 1024;

            // Calculate mainImageUrl and mainImagePath based on whether custom gallery images exist
            const hasGalleryImages = location?.images && location.images.length > 0;
            const primaryImage = location?.images.find(image => image.id === location.primaryImageId);
            const mainImageUrl = hasGalleryImages
                ? (primaryImage?.profile?.url || primaryImage?.original?.url || compositeUrl)
                : compositeUrl;
            const mainImagePath = hasGalleryImages
                ? (primaryImage?.profile?.path || primaryImage?.original?.path || compositePath)
                : compositePath;

            const updateData: Partial<Location> = {
                mapColorUrl: colorUrl,
                mapColorStoragePath: colorPath,
                type: inferredMapType,
                mapImageUrl: compositeUrl,
                mapImageStoragePath: compositePath,
                placedSymbols: placedSymbols,
                mapEntityPoints: world.mapEntityPoints,
                mapEntityAreas: world.mapEntityAreas,
                mapEntityPaths: paths.mapEntityPaths,
                mapLabels: labels.mapLabels,
                mapWidth: savedMapWidth,
                mapHeight: savedMapHeight,
                mainImageUrl,
                mainImagePath,
            };

            if (!isCustomUpload) {
                updateData.mapLandUrl = landUrl || undefined;
                updateData.mapLandStoragePath = landPath || undefined;
                updateData.mapSymbolsUrl = symbolsUrl || undefined;
                updateData.mapSymbolsStoragePath = symbolsPath || undefined;
                updateData.originalMapUrl = null;
                updateData.originalMapStoragePath = null;
            }

            await backend.entities.updateEntity(ownerId, novel.id, 'locations', locationId, updateData);

            // Keep the search and card views in sync by updating the location summary in Firestore
            await backend.entities.updateEntitySummary(ownerId, novel.id, 'locations', locationId, {
                id: locationId,
                name: location?.name || '',
                type: inferredMapType,
                shortDescription: location?.shortDescription || '',
                primaryImageId: location?.primaryImageId ?? null,
                mainImageUrl,
                mainImagePath,
                parentId: location?.parentId || undefined,
                graphPosition: location?.graphPosition,
                graphPositionUpdatedBy: location?.graphPositionUpdatedBy,
                graphPositionUpdatedAt: location?.graphPositionUpdatedAt,
                updatedAt: new Date().toISOString(),
            });

            // Clean up old storage files asynchronously
            const pathsToDelete = [
                oldLandPath,
                oldColorPath,
                oldSymbolsPath,
                (oldCompositePath !== oldOriginalPath ? oldCompositePath : null),
                (!isCustomUpload ? oldOriginalPath : null)
            ].filter(Boolean) as string[];

            Promise.all(pathsToDelete.map(path => backend.assets.deleteStorageFile(path)))
                .catch(err => console.warn("Failed to delete old storage assets:", err));

            toast({ title: t('map_creator.save_success_title'), description: t('map_creator.save_success_desc') });
        } catch (error: unknown) {
            console.error("Save Error:", error);
            toast({
                title: t('map_creator.save_failed_title'),
                description: error instanceof Error ? error.message : t('map_creator.save_failed_desc'),
                variant: 'destructive'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const inferredMapType = (mapType || location?.type || 'region').toLowerCase();
    const inferredMapStyle = (mapStyle || 'fantasy').toLowerCase();
    const isCustomUpload = !!location?.originalMapUrl;

    const showSpinner = isDocLoading || isCheckingForMap || (isAssetsLoading && (location?.mapLandUrl || location?.mapImageUrl));

    if (showSpinner) {
        return (
            <Box height="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="grey.900" flexDirection="column" gap={2}>
                <CircularProgress color="primary" />
                <Typography variant="caption" color="text.secondary">{t('map_creator.loading_studio')}</Typography>
                {isAssetsLoading && !isDocLoading && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
                        {t('map_creator.downloading_layers')}
                    </Typography>
                )}
            </Box>
        );
    }

    if (viewMode === 'canvas') {
        return (
            <Box p={2} height="calc(100vh - 180px)" display="flex" flexDirection="column" gap={2}>
                <PageHeader
                    title={t('map_creator.cartography_title', { name: location?.name || '' })}
                    description={t('map_creator.studio_desc')}
                    breadcrumbs={[
                        { label: t('map_creator.breadcrumbs.locations'), href: '/locations' },
                        { label: location?.name || t('entity_view.locations.view_fallback'), href: `/locations/view/${locationId}` },
                        { label: t('map_creator.breadcrumbs.map') }
                    ]}
                />

                <Box display="flex" gap={2} flex={1} minHeight={0} mt={2}>
                    <ToolbarSidebar
                        activeSection={activeSection}
                        setActiveSection={setActiveSection}
                        onSave={handleSaveGrid}
                        onUndo={undo}
                        onRedo={redo}
                        canUndo={canUndo}
                        canRedo={canRedo}
                        isSaving={isSaving}
                        activeSymbol={activeSymbol}
                        setActiveSymbol={setActiveSymbol}
                        isCustomUpload={isCustomUpload}
                    />

                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            bgcolor: 'grey.900',
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                            position: 'relative',
                            borderRadius: 2
                        }}
                    >
                        <MapCanvas
                            ref={canvasRef}
                            location={location}
                            activeSection={activeSection}
                            activeLandTool={activeLandTool}
                            paintOpacity={paintOpacity}
                            brush={brush}
                            brushSize={brushSize}
                            roughness={roughness}
                            tool={tool}
                            activeSymbol={activeSymbol}
                            activeSymbols={activeSymbols}
                            setActiveSymbol={setActiveSymbol}
                            activeSymbolCategory={activeSymbolCategory}
                            symbolMode={symbolMode}
                            symbolSize={symbolSize}
                            stampDensity={stampDensity}
                            onCommit={commitHistory}
                            placedSymbols={placedSymbols}
                            setPlacedSymbols={setPlacedSymbols}
                            // Asset Hoisting
                            isAssetsLoading={isAssetsLoading}
                            lastUpdated={lastUpdated}
                            landRef={landRef}
                            colorRef={colorRef}
                            symbolsRef={symbolsRef}
                            assemblerRef={assemblerRef}
                            patternsRef={patternsRef}
                            landMaskRef={landMaskRef}
                            stampMaskRef={stampMaskRef}
                            processedSheets={processedSheets}
                            customMapImageRef={customMapImageRef}
                            historyTick={historyTick}
                            // World entity
                            mapEntityPoints={world.mapEntityPoints}
                            setMapEntityPoints={world.setMapEntityPoints}
                            mapEntityAreas={world.mapEntityAreas}
                            setMapEntityAreas={world.setMapEntityAreas}
                            isPlacingPoint={world.isPlacingPoint}
                            isDrawingArea={world.isDrawingArea}
                            onPointPlaced={(x, y) => { world.handlePointPlaced(x, y); commitHistory(); }}
                            onAreaDrawn={world.handleAreaDrawn}
                            selectedWorldUid={world.selectedWorldUid}
                            setSelectedWorldUid={world.setSelectedWorldUid}
                            inProgressVertices={world.inProgressVertices}
                            cursorVertex={world.cursorVertex}
                            setCursorVertex={world.setCursorVertex}
                            addVertex={world.addVertex}
                            finishDrawing={() => { world.finishDrawing(); commitHistory(); }}
                            // Paths
                            mapEntityPaths={paths.mapEntityPaths}
                            setMapEntityPaths={paths.setMapEntityPaths}
                            isDrawingPath={paths.isDrawingPath}
                            selectedPathUid={paths.selectedPathUid}
                            setSelectedPathUid={paths.setSelectedPathUid}
                            inProgressPathVertices={paths.inProgressPathVertices}
                            pathCursorVertex={paths.pathCursorVertex}
                            setPathCursorVertex={paths.setPathCursorVertex}
                            addPathVertex={paths.addPathVertex}
                            finishPathDrawing={(finalSnap) => { paths.finishPathDrawing(finalSnap); commitHistory(); }}
                            movePathVertex={paths.moveVertex}
                            insertPathVertex={paths.insertVertex}
                            deletePathVertex={paths.deleteVertex}
                            activePathTypeRef={paths.activePathTypeRef}
                            // Labels
                            mapLabels={labels.mapLabels}
                            setMapLabels={labels.setMapLabels}
                            selectedLabelUid={labels.selectedLabelUid}
                            setSelectedLabelUid={labels.setSelectedLabelUid}
                            // Layers
                            layerVisibility={layerVisibility}
                        />
                        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                            <Chip
                                label={`${t(`map_creator.types.${inferredMapType}`)} / ${t(`map_creator.styles.${inferredMapStyle.replace('-', '_')}`)}`}
                                sx={{ bgcolor: 'background.paper', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}
                                size="small"
                            />
                        </Box>
                    </Paper>

                    <ToolbarPanel
                        activeSection={activeSection}
                        activeLandTool={activeLandTool}
                        setActiveLandTool={setActiveLandTool}
                        paintOpacity={paintOpacity}
                        setPaintOpacity={setPaintOpacity}
                        brush={brush}
                        setBrush={setBrush}
                        brushSize={brushSize}
                        setBrushSize={setBrushSize}
                        roughness={roughness}
                        setRoughness={setRoughness}
                        tool={tool}
                        setTool={setTool}
                        onZoomIn={() => canvasRef.current?.zoomIn()}
                        onZoomOut={() => canvasRef.current?.zoomOut()}
                        onZoomReset={() => canvasRef.current?.resetView()}
                        activeSymbol={activeSymbol}
                        activeSymbols={activeSymbols}
                        setActiveSymbol={setActiveSymbol}
                        setActiveSymbols={setActiveSymbols}
                        activeSymbolCategory={activeSymbolCategory}
                        setActiveSymbolCategory={setActiveSymbolCategory}
                        symbolMode={symbolMode}
                        setSymbolMode={setSymbolMode}
                        stampDensity={stampDensity}
                        setStampDensity={setStampDensity}
                        symbolSize={symbolSize}
                        setSymbolSize={setSymbolSize}
                        processedSheets={processedSheets}
                        placedSymbols={placedSymbols}
                        // World props
                        mapEntityPoints={world.mapEntityPoints}
                        mapEntityAreas={world.mapEntityAreas}
                        isPlacingPoint={world.isPlacingPoint}
                        isDrawingArea={world.isDrawingArea}
                        onStartPlacingPoint={() => { world.setIsPlacingPoint(true); world.setIsDrawingArea(false); }}
                        onStopPlacingPoint={() => world.setIsPlacingPoint(false)}
                        onStartDrawingArea={() => { world.setIsDrawingArea(true); world.setIsPlacingPoint(false); }}
                        onStopDrawingArea={() => world.setIsDrawingArea(false)}
                        onDeletePoint={(uid) => { world.deletePoint(uid); commitHistory(); }}
                        onDeleteArea={(uid) => { world.deleteArea(uid); commitHistory(); }}
                        onChangeLabelPoint={(uid, label) => { world.changeLabelPoint(uid, label); commitHistory(); }}
                        onChangeEntityPoint={(uid, ref) => { world.changeEntityPoint(uid, ref); commitHistory(); }}
                        onChangeEntityArea={(uid, ref) => { world.changeEntityArea(uid, ref); commitHistory(); }}
                        onUpdateAreaStyle={(uid, style) => { world.updateAreaStyle(uid, style); commitHistory(); }}
                        selectedWorldUid={world.selectedWorldUid}
                        onSelectWorldUid={world.setSelectedWorldUid}
                        currentLocationId={locationId}
                        // Paths
                        mapEntityPaths={paths.mapEntityPaths}
                        isDrawingPath={paths.isDrawingPath}
                        selectedPathUid={paths.selectedPathUid}
                        onSelectPath={paths.setSelectedPathUid}
                        onStartDrawingPath={paths.startDrawingPath}
                        onStopDrawingPath={paths.cancelPathDrawing}
                        onDeletePath={(uid) => { paths.deletePath(uid); commitHistory(); }}
                        onUpdatePathStyle={(uid, style) => { paths.updatePathStyle(uid, style); commitHistory(); }}
                        onUpdatePathMeta={(uid, meta) => { paths.updatePathMeta(uid, meta); commitHistory(); }}
                        onUpdatePathType={(uid, type, quality) => { paths.updatePathType(uid, type, quality); commitHistory(); }}
                        // Labels
                        mapLabels={labels.mapLabels}
                        selectedLabelUid={labels.selectedLabelUid}
                        onUpdateLabel={(uid, updates) => { labels.updateLabel(uid, updates); commitHistory(); }}
                        onDeleteLabel={(uid) => { labels.deleteLabel(uid); commitHistory(); }}
                        onCommitLabel={commitHistory}
                        // Layers
                        layerVisibility={layerVisibility}
                        setLayerVisibility={setLayerVisibility}
                    />

                    {/* Entity picker for point/area linking after placement */}
                    <WorldEntityPicker
                        open={world.entityPickerOpen}
                        onClose={world.closePicker}
                        onSelect={(ref) => { world.handleEntityPicked(ref); commitHistory(); }}
                        locationsOnly={world.entityPickerLocationsOnly}
                        currentLocationId={locationId}
                    />
                </Box>
            </Box>
        )
    }

    return (
        <Box p={4} display="flex" flexDirection="column" gap={4}>
            <Box>
                <Typography variant="h4" component="h1" gutterBottom>{t('map_creator.creator_title')}</Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('map_creator.creator_desc')}
                </Typography>
            </Box>

            <Paper sx={{ maxWidth: 600, mx: 'auto', width: '100%', p: 4, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom>{t('map_creator.settings_title')}</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    {t('map_creator.settings_desc')}
                </Typography>

                <Stack spacing={3} mt={4}>
                    <FormControl fullWidth>
                        <InputLabel id="map-type-label">{t('map_creator.map_type')}</InputLabel>
                        <Select
                            labelId="map-type-label"
                            value={mapType || ''}
                            label={t('map_creator.map_type')}
                            onChange={(e) => setMapType(e.target.value)}
                        >
                            <MenuItem value="continent">{t('map_creator.types.continent')}</MenuItem>
                            <MenuItem value="region">{t('map_creator.types.region')}</MenuItem>
                            <MenuItem value="city">{t('map_creator.types.city')}</MenuItem>
                            <MenuItem value="village" disabled>{t('map_creator.types.village')}</MenuItem>
                            <MenuItem value="floorplan" disabled>{t('map_creator.types.floorplan')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="map-style-label">{t('map_creator.style_era')}</InputLabel>
                        <Select
                            labelId="map-style-label"
                            value={mapStyle}
                            label={t('map_creator.style_era')}
                            onChange={(e) => setMapStyle(e.target.value)}
                        >
                            <MenuItem value="fantasy">{t('map_creator.styles.fantasy')}</MenuItem>
                            <MenuItem value="dark-fantasy" disabled>{t('map_creator.styles.dark_fantasy')}</MenuItem>
                            <MenuItem value="sci-fi" disabled>{t('map_creator.styles.sci_fi')}</MenuItem>
                            <MenuItem value="modern" disabled>{t('map_creator.styles.modern')}</MenuItem>
                            <MenuItem value="cyberpunk" disabled>{t('map_creator.styles.cyberpunk')}</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => setViewMode('canvas')}
                        disabled={!mapType}
                        fullWidth
                    >
                        {t('map_creator.proceed_to_canvas')}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}

function MapCreatorPage() {
    const { id } = Route.useParams();

    return (
        <AuthGuard>
            <FeatureGuard featureKey="should_display_map_feature">
                <Suspense fallback={<Box p={4}>Loading map creator...</Box>}>
                    <MapCreatorContent locationId={id} />
                </Suspense>
            </FeatureGuard>
        </AuthGuard>
    );
}
